/**
 * Authentication Service
 *
 * Handles all authentication-related API calls
 */

import Cookies from 'js-cookie';
import api from '@utils/api';
import config from '@/config';
import { getDeviceInfoHeader } from '@utils/deviceInfo';

// Cookie options
const COOKIE_OPTIONS = {
  expires: 7, // 7 days
  secure: config.isProduction, // Only use secure in production (HTTPS)
  sameSite: 'strict', // CSRF protection
  path: '/',
};

const authService = {
  /**
   * Login user
   * @param {string} username - Username
   * @param {string} password - Password
   * @returns {Promise} Login response with token and user data
   */
  login: async (username, password) => {
    try {
      // Get device information for header
      const deviceInfo = getDeviceInfoHeader();

      const response = await api.post(
        config.endpoints.auth.login,
        {
          username,
          password,
        },
        {
          headers: {
            'X-Device-Info': deviceInfo,
          },
        }
      );

      // Store token in cookie if provided
      if (response.token) {
        Cookies.set('token', response.token, COOKIE_OPTIONS);
      }

      // Store refresh token in cookie if provided
      if (response.refreshToken) {
        Cookies.set('refreshToken', response.refreshToken, COOKIE_OPTIONS);
      }

      // Store user data in cookie if provided
      if (response.user) {
        Cookies.set('user', JSON.stringify(response.user), COOKIE_OPTIONS);
      }

      // Note: Permissions are not returned from login API
      // They should be fetched from /me endpoint after login

      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Logout user
   * @returns {Promise}
   */
  logout: async () => {
    try {
      await api.post(config.endpoints.auth.logout);

      // Clear cookies
      Cookies.remove('token', { path: '/' });
      Cookies.remove('refreshToken', { path: '/' });
      Cookies.remove('user', { path: '/' });
      Cookies.remove('permissions', { path: '/' });

      return { success: true };
    } catch (error) {
      // Clear cookies even if API call fails
      Cookies.remove('token', { path: '/' });
      Cookies.remove('refreshToken', { path: '/' });
      Cookies.remove('user', { path: '/' });
      Cookies.remove('permissions', { path: '/' });
      throw error;
    }
  },

  /**
   * Get current user info
   * @returns {Promise} User data
   */
  me: async () => {
    try {
      const response = await api.get(config.endpoints.auth.me);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Fetch current user info and update cookies
   * @returns {Promise} User data with permissions
   */
  fetchMe: async () => {
    try {
      const response = await api.get(config.endpoints.auth.me);

      // Update access token in cookie if provided
      if (response.accessToken) {
        Cookies.set('token', response.accessToken, COOKIE_OPTIONS);
      }

      // Update refresh token in cookie if provided
      if (response.refreshToken) {
        Cookies.set('refreshToken', response.refreshToken, COOKIE_OPTIONS);
      }

      // Update user data in cookie if provided
      // Backend may return userInfo, user, or individual fields at root level
      let userData = null;
      if (response.userInfo) {
        userData = response.userInfo;
      } else if (response.user) {
        userData = response.user;
      } else if (response.username) {
        userData = {
          id: response.id,
          username: response.username,
          email: response.email,
          fullName: response.fullName,
          phone: response.phone,
          status: response.status,
          roleId: response.roleId,
        };
      }

      if (userData) {
        Cookies.set('user', JSON.stringify(userData), COOKIE_OPTIONS);
      }

      // Update permissions in cookie if provided
      if (response.permissions) {
        const permissionsJson = JSON.stringify(response.permissions);
        Cookies.set('permissions', permissionsJson, COOKIE_OPTIONS);
      }

      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Refresh authentication token
   * @returns {Promise} New token
   */
  refreshToken: async () => {
    try {
      const refreshToken = Cookies.get('refreshToken');

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post(config.endpoints.auth.refresh, {
        refreshToken,
      });

      // Update token in cookie
      if (response.token) {
        Cookies.set('token', response.token, COOKIE_OPTIONS);
      }

      // Update refresh token in cookie if provided
      if (response.refreshToken) {
        Cookies.set('refreshToken', response.refreshToken, COOKIE_OPTIONS);
      }

      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Send OTP code via SMS or Email for 2FA
   * @param {string} username - Username
   * @param {number} method - 2FA method: 1 for SMS, 2 for Email
   * @returns {Promise} Response with message
   */
  sendOtp: async (username, method) => {
    try {
      const response = await api.post(config.endpoints.twoFactor.sendOtp, {
        username,
        method, // 1=SMS, 2=Email
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Login with 2FA code
   * @param {string} username - Username
   * @param {string} password - Password
   * @param {string} twoFactorCode - OTP code
   * @param {boolean} useRecoveryCode - Whether using recovery code instead of OTP
   * @returns {Promise} Response with token and user data
   */
  loginWith2FA: async (username, password, twoFactorCode, useRecoveryCode = false) => {
    try {
      // Get device information for header
      const deviceInfo = getDeviceInfoHeader();

      const response = await api.post(
        config.endpoints.auth.login,
        {
          username,
          password,
          twoFactorCode,
          useRecoveryCode,
        },
        {
          headers: {
            'X-Device-Info': deviceInfo,
          },
        }
      );

      // Store token in cookie if provided
      if (response.token) {
        Cookies.set('token', response.token, COOKIE_OPTIONS);
      }

      // Store refresh token in cookie if provided
      if (response.refreshToken) {
        Cookies.set('refreshToken', response.refreshToken, COOKIE_OPTIONS);
      }

      // Store user data in cookie if provided
      if (response.user) {
        Cookies.set('user', JSON.stringify(response.user), COOKIE_OPTIONS);
      }

      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated: () => {
    const token = Cookies.get('token');
    return !!token;
  },

  /**
   * Get stored token
   * @returns {string|null}
   */
  getToken: () => {
    return Cookies.get('token') || null;
  },

  /**
   * Get stored user data
   * @returns {Object|null}
   */
  getUser: () => {
    const user = Cookies.get('user');
    return user ? JSON.parse(user) : null;
  },

  /**
   * Get stored permissions
   * @returns {Array|null}
   */
  getPermissions: () => {
    const permissions = Cookies.get('permissions');
    return permissions ? JSON.parse(permissions) : null;
  },
};

export default authService;
